import * as React from "react";
import { useTranslation } from "react-i18next";
import { useWindowDimensions, StyleSheet } from "react-native";
import { Paragraph, useTheme, Title, ActivityIndicator } from "react-native-paper";
import { useSelector } from "react-redux";
import { CARDITEM } from "../../../services/services-model.type";
import { spacing } from "@styles/spacing";
import { formatCurrency } from "../../../util/format-currency";
import { useMobileMediaQuery } from "@media-query";
import { Block } from "@block";
import ItemCardModal from "./card-content";
import { RecyclerListView, DataProvider, LayoutProvider } from "recyclerlistview";
import { Constant } from "app/shared/constant/constant";
import { getCardListSelector, getStatusListCardSelector } from "app/modules/card-manager/reducer/selectors";

interface CardSourceProps {
  title?: string;
  card?: CARDITEM;
  typeArray?: Array<string>;
  errorMessage?: string;
  onPressAcceptCard?: Function;
  statusArrayCredit?: Array<string>;
  statusArrayDebit?: Array<string>;
}

export interface FlatListItemCard {
  data?: CARDITEM;
  type?: string;
  label?: string;
  value?: string;
  header?: { left: string; right: string };
  alphabet?: string;
}

const ViewTypes = {
  HEADER: 0,
  LISTITEM: 1,
};

let arrayHeader: Array<number> = [];

const CardSourceModal = (props: CardSourceProps) => {
  const { title, typeArray = [], errorMessage, onPressAcceptCard = () => {}, statusArrayCredit = [], statusArrayDebit = [] } = props;
  const { t } = useTranslation();
  const { colors, fonts } = useTheme();
  const { width, height } = useWindowDimensions();
  const isMobile = useMobileMediaQuery();

  const listCardStore = useSelector(getCardListSelector);
  const statusRequest = useSelector(getStatusListCardSelector);
  const [listData, setListData] = React.useState<FlatListItemCard[]>([]);

  const [dataProvider] = React.useState<DataProvider>(
    new DataProvider((r1, r2) => {
      return r1 !== r2;
    }),
  );

  const [layoutProvider] = React.useState<LayoutProvider>(
    new LayoutProvider(
      (index) => {
        if (arrayHeader.length && arrayHeader.includes(index)) {
          return ViewTypes.HEADER;
        }
        return ViewTypes.LISTITEM;
      },
      (type, dim) => {
        switch (type) {
          case ViewTypes.HEADER:
            dim.width = isMobile ? width * 0.9 : 550 - spacing.huge * 2;
            dim.height = spacing.macro;
            break;
          case ViewTypes.LISTITEM:
            dim.width = isMobile ? width * 0.9 : 550 - spacing.huge * 2;
            dim.height = isMobile ? spacing.galactic + 30 : spacing.galactic;
            break;
          default:
            dim.width = 0;
            dim.height = 0;
            break;
        }
      },
    ),
  );

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        flatList: {
          flex: 1,
          paddingHorizontal: spacing.huge,
        },
        titleModal: {
          color: colors.accent,
          fontFamily: fonts.medium.fontFamily,
        },
        modalContainerStyle: {
          alignSelf: "center",
          height: height * 0.8,
          borderRadius: 10,
        },
      }),
    [colors.accent, fonts.medium.fontFamily, height],
  );

  const onPressChoose = (item: FlatListItemCard) => {
    onPressAcceptCard(item.data);
  };

  const getListCardCredit = React.useCallback(
    (dataStore?: CARDITEM[]) => {
      if (dataStore?.length) {
        let data: CARDITEM[] | undefined = dataStore;
        if (dataStore.length && statusArrayCredit.length) {
          data = dataStore.filter((item) => {
            return item.hotStatus && statusArrayCredit.includes(item.hotStatus);
          });
        }
        const filter = data.filter((item) => {
          if (typeArray.length) {
            return item.cardType?.length && typeArray.includes(item.cardType);
          } else if (typeArray.length) {
            return item.cardType?.length && typeArray.includes(item.cardType || "");
          } else {
            return item.cardType?.length;
          }
        });
        return filter;
      }
      return [];
    },
    [typeArray, statusArrayCredit],
  );

  const mapDataListCard = React.useCallback(
    (dataStore?: CARDITEM[]) => {
      const _listCardCredit: FlatListItemCard[] = [];
      const _listCardDebit: FlatListItemCard[] = [];
      const _listCardDebitMaster: FlatListItemCard[] = [];
      if (dataStore?.length) {
        let data: CARDITEM[] | undefined = dataStore;
        if (dataStore.length && (statusArrayCredit.length || statusArrayDebit.length)) {
          data = dataStore.filter((item) => {
            return (
              item.hotStatus &&
              ((item.cardType === Constant.CARD.CARD_TYPE.CREDIT && statusArrayCredit.includes(item.hotStatus)) ||
                ((item.cardType === Constant.CARD.CARD_TYPE.DEBIT || item.cardType === Constant.CARD.CARD_TYPE.DEBIT_MASTER) &&
                  statusArrayDebit.includes(item.hotStatus)))
            );
          });
        }
        data.forEach((_card: CARDITEM) => {
          const obj: FlatListItemCard = {
            data: _card,
            type: _card.cardType,
            label: _card.cardNum || "",
            value: _card.cardType === Constant.CARD.CARD_TYPE.CREDIT ? formatCurrency(_card?.avalBalance) : "",
          };
          if (_card.cardType === Constant.CARD.CARD_TYPE.CREDIT) _listCardCredit.push(obj);
          if (_card.cardType === Constant.CARD.CARD_TYPE.DEBIT) _listCardDebit.push(obj);
          if (_card.cardType === Constant.CARD.CARD_TYPE.DEBIT_MASTER) _listCardDebitMaster.push(obj);
          return _card;
        });
      }
      const data = { CREDIT: _listCardCredit, DEBIT: _listCardDebit, DEBIT_MASTER: _listCardDebitMaster };
      return data;
    },
    [statusArrayCredit, statusArrayDebit],
  );

  const getDataListCardAll = React.useCallback(
    (dataStore?: CARDITEM[]) => {
      let dataList: FlatListItemCard[] = [];
      arrayHeader = [];
      const data = mapDataListCard(dataStore);
      if (data.CREDIT.length) {
        arrayHeader = [0];
        dataList.push({
          header: { left: t("CARD_THE_TIN_DUNG"), right: t("ACC_SO_DU_KHA_DUNG") },
        });
      }
      dataList = dataList.concat(data.CREDIT);

      if (data.DEBIT.length && dataList.length) {
        arrayHeader.push(dataList.length);
      }
      if (data.DEBIT.length) {
        dataList.push({
          header: { left: t("CARD_THE_GHI_NO_NOI_DIA"), right: "" },
        });
      }
      dataList = dataList.concat(data.DEBIT);

      if (data.DEBIT_MASTER.length && dataList.length) {
        arrayHeader.push(dataList.length);
      }
      if (data.DEBIT_MASTER.length) {
        dataList.push({
          header: { left: t("CARD_THE_GHI_NO_QUOC_TE"), right: "" },
        });
      }
      dataList = dataList.concat(data.DEBIT_MASTER);
      return dataList;
    },
    [mapDataListCard, t],
  );

  const getDataListCardCredit = React.useCallback(
    (dataStore?: CARDITEM[]) => {
      let list: FlatListItemCard[] = [];
      arrayHeader = [];
      const listCard = getListCardCredit(dataStore);
      if (listCard.length) {
        arrayHeader = [0];
        list.push({
          header: { left: t("CARD_THE_TIN_DUNG"), right: t("ACC_SO_DU_KHA_DUNG") },
        });
        listCard.forEach((_card: CARDITEM) => {
          const obj: FlatListItemCard = {
            data: _card,
            type: Constant.CARD.CARD_TYPE.CREDIT,
            label: _card.cardNum || "",
            value: formatCurrency(_card.avalBalance),
          };
          list.push(obj);
        });
      }
      return list;
    },
    [getListCardCredit, t],
  );

  const getDataListCard = React.useCallback(
    (dataStore?: CARDITEM[]) => {
      return !typeArray.length ? getDataListCardAll(dataStore) : getDataListCardCredit(dataStore);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [typeArray.length],
  );

  const getDataProvider = React.useCallback(() => {
    return dataProvider.cloneWithRows(listData);
  }, [dataProvider, listData]);

  React.useEffect(() => {
    if (listCardStore && listCardStore?.length >= 0) {
      const _listCard = getDataListCard(listCardStore);
      console.log("listDataNew: ", _listCard);
      if (_listCard.length) {
        setListData(_listCard);
      }
    } else {
      setListData([]);
    }
  }, [getDataListCard, listCardStore]);

  const TitleModal = (
    <React.Fragment>
      <Block ph={spacing.huge} pv={spacing.medium}>
        <Title style={styles.titleModal}>{title || t("CARD_TAI_KHOAN_THE")}</Title>
      </Block>
    </React.Fragment>
  );

  const FormEmpty =
    statusRequest !== "init" && !listData.length ? (
      <Block pv={spacing.massive} center middle>
        <Paragraph style={[fonts.regular, { color: colors.text }]}>{errorMessage || t("CARD_BAN_CHUA_DANG_KI_LOAI_THE_NAY")}</Paragraph>
      </Block>
    ) : null;

  const renderFooter =
    statusRequest === "pending" ? (
      <Block center middle pv={spacing.small}>
        <ActivityIndicator color={colors.primary} size={"small"} />
      </Block>
    ) : (
      FormEmpty
    );

  const renderItem = (type: string | number, item: any, index: number) => {
    return <ItemCardModal item={item} index={index} onClick={onPressChoose} />;
  };

  return (
    <>
      {TitleModal}
      <RecyclerListView
        style={styles.flatList}
        dataProvider={getDataProvider()}
        layoutProvider={layoutProvider}
        onScroll={({ nativeEvent: { contentSize, layoutMeasurement, contentOffset } }) => {
          const distanceFromEnd = (contentSize?.height || 0) - (layoutMeasurement?.height || 0) - contentOffset.y;
          if (distanceFromEnd < 0) return false;
        }}
        renderAheadOffset={0}
        rowRenderer={renderItem}
        renderFooter={() => renderFooter}
      />
    </>
  );
};

export default CardSourceModal;
